/**
 * RecordsManagementShell — top tabs + role gate for the SuperAdmin Records
 * Management surface.
 *
 * Role gating (per Wave 6.3 brief §D): we check the auth context for
 * `roles` containing 'SuperAdmin'. If the caller lacks the role, the surface
 * renders a friendly "Restricted" panel instead of the inner tabs. The
 * routes themselves stay defined so that an admin escalation can simply
 * refresh their token to access them.
 */

import { NavLink, Outlet } from 'react-router';
import { Pill } from '@/components/primitives';
import { useAuth } from '@/contexts/AuthContext';

interface TabDef {
  to: string;
  label: string;
  description: string;
}

// Absolute: this shell is a pathless layout under the /super-admin/records/* splat, where React Router 7
// resolves relative links from the full address (v7_relativeSplatPath), so 'browser' would nest.
const RECORDS_BASE = '/super-admin/records';

const TABS: ReadonlyArray<TabDef> = [
  {
    to: `${RECORDS_BASE}/browser`,
    label: 'Records browser',
    description: 'Filter, bulk-select, hard-delete, or correct records across cart and maintenance domains.',
  },
  {
    to: `${RECORDS_BASE}/test-mode-wipe`,
    label: 'Test Mode wipe',
    description: 'One-click clear of every isTestMode row across carts, products, and maintenance actions.',
  },
  {
    to: `${RECORDS_BASE}/purge-history`,
    label: 'Purge history',
    description: 'Append-only audit of every hard-delete since ROP launch.',
  },
  {
    to: `${RECORDS_BASE}/recovery`,
    label: 'Recovery',
    description: 'Restore a previously-purged record from its SharePoint JSON snapshot.',
  },
  {
    to: `${RECORDS_BASE}/retention-holds`,
    label: 'Retention holds',
    description: 'Place / release legal holds that keep a cart restorable past its policy window.',
  },
];

function isSuperAdmin(roles: ReadonlyArray<string> | undefined): boolean {
  if (!roles) return false;
  return roles.some((r) => r.toLowerCase() === 'superadmin');
}

export function RecordsManagementShell(): JSX.Element {
  const { user } = useAuth();
  const allowed = isSuperAdmin(user?.roles);

  return (
    <div className="module-page" style={{ padding: 'var(--sp-7)' }}>
      <header
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-3)',
          marginBottom: 'var(--sp-7)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-4)' }}>
          <h1 style={{ margin: 0, fontSize: 22 }}>Records Management</h1>
          <Pill variant="warn">SuperAdmin only</Pill>
        </div>
        <p style={{ margin: 0, color: 'var(--ink-700)', fontSize: 13, maxWidth: 760, lineHeight: 1.55 }}>
          Hard-delete, bulk-correct, and recover records from across the Retail Operations Platform. Every
          destructive action snapshots the affected entity to SharePoint{' '}
          <code>ao_PurgeRecords</code> before deletion and writes an immutable PurgeHistory row —
          so a record removed by mistake can be restored from the recovery tab.
        </p>
      </header>

      {!allowed ? (
        <RestrictedPanel />
      ) : (
        <>
          <nav
            role="tablist"
            aria-label="Records management tabs"
            style={{
              display: 'flex',
              gap: 'var(--sp-2)',
              borderBottom: '1px solid var(--surface-border)',
              marginBottom: 'var(--sp-6)',
            }}
          >
            {TABS.map((t) => (
              <NavLink
                key={t.to}
                to={t.to}
                role="tab"
                style={({ isActive }) => ({
                  padding: 'var(--sp-3) var(--sp-5)',
                  fontSize: 13,
                  color: isActive ? 'var(--pcb-blue)' : 'var(--ink-700)',
                  fontWeight: isActive ? 600 : 500,
                  textDecoration: 'none',
                  borderBottom: isActive ? '2px solid var(--pcb-blue)' : '2px solid transparent',
                })}
                title={t.description}
              >
                {t.label}
              </NavLink>
            ))}
          </nav>
          <Outlet />
        </>
      )}
    </div>
  );
}

function RestrictedPanel(): JSX.Element {
  return (
    <div
      role="alert"
      style={{
        padding: 'var(--sp-8)',
        background: 'var(--surface-card)',
        border: '1px dashed var(--ink-300)',
        borderRadius: 'var(--r-md)',
        color: 'var(--ink-700)',
        fontSize: 14,
        textAlign: 'center',
      }}
    >
      <h2 style={{ margin: '0 0 var(--sp-3)', fontSize: 16 }}>Restricted — SuperAdmin access required</h2>
      <p style={{ margin: 0, lineHeight: 1.5 }}>
        Records Management is gated to the SuperAdmin role. Ask your retail-ops manager to grant
        the role through the Admin → Users editor and refresh.
      </p>
    </div>
  );
}
